import React from 'react';
import { Link } from 'react-router-dom';
import {
  HomeIcon,
  ShoppingCartIcon,
  DocumentIcon,
  TruckIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  PresentationChartLineIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  TagIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  BanknotesIcon,
  RectangleStackIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { usePermissoes } from '../hooks/usePermissoes';
import { useUser } from '../hooks/useAuth';
import { isPermissaoDisponivel } from '../types/usuarios';

interface RecursoCard {
  to: string;
  icon: React.ReactNode;
  titulo: string;
  descricao: string;
  permissao?: string;
  admin?: boolean;
  /** Cor do tile do ícone (launcher colorido por categoria). */
  cor: string;
}

/**
 * Cards de TODOS os recursos do sistema. Renderizados condicionalmente
 * baseado nas permissões do usuário logado — sem permissão, o card some.
 *
 * Pra adicionar um recurso novo: adiciona aqui + Sidebar + ROTA_PERMISSAO
 * (em ProtectedRoute). O usuário vê o card e clica → ProtectedRoute valida
 * a permissão antes de renderizar a página.
 */
const RECURSOS: RecursoCard[] = [
  // Operacional
  { to: '/dashboard', icon: <PresentationChartLineIcon className="h-8 w-8" />, titulo: 'Dashboard',
    descricao: 'Visão geral de vendas, métricas e KPIs', permissao: 'dashboard', cor: 'bg-blue-500' },
  { to: '/pedidos', icon: <ShoppingCartIcon className="h-8 w-8" />, titulo: 'Pedidos',
    descricao: 'Acompanhamento e gestão de pedidos', permissao: 'pedidos', cor: 'bg-emerald-500' },
  { to: '/xml-terceiros', icon: <DocumentIcon className="h-8 w-8" />, titulo: 'NF-e Terceiros',
    descricao: 'Consulta e download de NFe de fornecedores', permissao: 'nfe_terceiros', cor: 'bg-purple-500' },
  { to: '/cte-terceiros', icon: <TruckIcon className="h-8 w-8" />, titulo: 'CT-e Terceiros',
    descricao: 'Monitoramento de CT-e de transportadoras', permissao: 'cte_terceiros', cor: 'bg-amber-500' },
  { to: '/conferencia', icon: <ClipboardDocumentCheckIcon className="h-8 w-8" />, titulo: 'Conferência NFe',
    descricao: 'Conferência de notas fiscais recebidas', permissao: 'conferencia_nfe', cor: 'bg-cyan-500' },

  // Catálogo
  { to: '/gerarCatalogo', icon: <DocumentIcon className="h-8 w-8" />, titulo: 'Gerar Catálogo',
    descricao: 'Gera catálogo de produtos em PDF', permissao: 'gerar_catalogo', cor: 'bg-pink-500' },
  { to: '/catalogoDigital', icon: <HomeIcon className="h-8 w-8" />, titulo: 'Catálogo Online',
    descricao: 'Catálogo digital público (PWA)', permissao: 'catalogo_online', cor: 'bg-rose-500' },
  { to: '/agrupamentos', icon: <UserGroupIcon className="h-8 w-8" />, titulo: 'Agrupamentos',
    descricao: 'Agrupamentos especiais de produtos', permissao: 'agrupamentos', cor: 'bg-fuchsia-500' },

  // Compras
  { to: '/compras/reposicao', icon: <PresentationChartLineIcon className="h-8 w-8" />, titulo: 'Reposição',
    descricao: 'Painel 360 das marcas com análise de estoque', permissao: 'compras.reposicao', cor: 'bg-indigo-500' },
  { to: '/compras/analise', icon: <ChartBarIcon className="h-8 w-8" />, titulo: 'Gerar Sugestão',
    descricao: 'Workflow de criação de sugestão de compra', permissao: 'compras.analise', cor: 'bg-blue-600' },
  { to: '/compras/sugestoes', icon: <ClipboardDocumentCheckIcon className="h-8 w-8" />, titulo: 'Sugestões',
    descricao: 'Lista de sugestões salvas pra gerar pedidos', permissao: 'compras.sugestoes', cor: 'bg-violet-500' },
  { to: '/compras/cotacoes', icon: <ClipboardDocumentListIcon className="h-8 w-8" />, titulo: 'Cotações',
    descricao: 'Cotações com fornecedores via link público', permissao: 'compras.cotacao', cor: 'bg-teal-500' },
  { to: '/compras/pedidos', icon: <ShoppingCartIcon className="h-8 w-8" />, titulo: 'Pedidos de Compra',
    descricao: 'Pedidos gerados e gestão de NFe recebidas', permissao: 'compras.pedido', cor: 'bg-emerald-600' },
  { to: '/compras/metas', icon: <BanknotesIcon className="h-8 w-8" />, titulo: 'Metas Mensais',
    descricao: 'Orçamento de compra mensal vs realizado por empresa', permissao: 'compras.metas', cor: 'bg-cyan-600' },
  { to: '/compras/metas/dimensao', icon: <RectangleStackIcon className="h-8 w-8" />, titulo: 'Metas por Dimensão',
    descricao: 'Meta detalhada por fornecedor/marca/grupo', permissao: 'compras.metas', cor: 'bg-cyan-700' },
  { to: '/compras/fornecedores', icon: <BuildingOffice2Icon className="h-8 w-8" />, titulo: 'Gestão de Fornecedores',
    descricao: 'Classificação Principal/Local + política comercial', permissao: 'compras.fornecedores', cor: 'bg-amber-500' },
  { to: '/compras/marcas', icon: <TagIcon className="h-8 w-8" />, titulo: 'Gestão de Marcas',
    descricao: 'Política comercial + histórico 12M por marca', permissao: 'compras.marcas', cor: 'bg-violet-500' },

  // Sistema
  { to: '/usuarios', icon: <UserGroupIcon className="h-8 w-8" />, titulo: 'Usuários',
    descricao: 'Cadastro de usuários e permissões', permissao: 'gerenciar_usuarios', cor: 'bg-zinc-500' },
  { to: '/auditoria', icon: <ShieldCheckIcon className="h-8 w-8" />, titulo: 'Auditoria',
    descricao: 'Histórico de eventos (login, cotação, pedido, alteração de dados)', permissao: 'auditoria', cor: 'bg-rose-600' },
  { to: '/organizacao', icon: <Cog6ToothIcon className="h-8 w-8" />, titulo: 'Organização',
    descricao: 'Dados da empresa, integrações e parâmetros', admin: true, cor: 'bg-zinc-700' },
  { to: '/perfil', icon: <UserCircleIcon className="h-8 w-8" />, titulo: 'Meu Perfil',
    descricao: 'Atualizar nome, email e senha', cor: 'bg-emerald-600' },
];

const HomePage: React.FC = () => {
  const { temPermissao, isAdmin } = usePermissoes();
  const { data: user } = useUser();

  const disponiveis = RECURSOS.filter(r => {
    // Feature 'disabled' some pra todos, inclusive ADMIN.
    if (r.permissao && !isPermissaoDisponivel(r.permissao)) return false;
    if (r.admin) return isAdmin;
    if (r.permissao) return temPermissao(r.permissao);
    return true;
  });

  return (
    <div className="mx-auto max-w-[1280px] space-y-5">
      <div>
        <h1 className="text-[22px] font-bold tracking-[-0.02em] text-tx">
          {user?.nome ? `Olá, ${user.nome.split(' ')[0]}` : 'Bem-vindo'}
        </h1>
        <p className="mt-0.5 text-[13.5px] text-tx-2">
          Selecione um recurso pra começar.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {disponiveis.map((r) => (
          <Link
            key={r.to}
            to={r.to}
            className="group flex items-start gap-3 rounded-iris-lg border border-line bg-surface p-4 transition-colors hover:bg-surface-2"
          >
            <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-iris text-white shadow-sm ${r.cor}`}>
              {r.icon}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-tx group-hover:text-accent-text">
                {r.titulo}
              </div>
              <div className="mt-0.5 text-xs leading-snug text-tx-3">
                {r.descricao}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {disponiveis.length === 0 && (
        <div className="rounded-iris-lg border border-warning-border bg-warning-soft p-6 text-center">
          <p className="text-sm text-warning">
            Você não tem permissão pra nenhum recurso ainda. Peça pro administrador liberar acesso em <strong>Usuários → Permissões</strong>.
          </p>
        </div>
      )}
    </div>
  );
};

export default HomePage;
